import { useMemo, useState, Fragment } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import { formatCurrencySinSignoPeso, formatDate } from '../../../components/utils/formatter'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Text } from '../../../components/modules/ui'
import { colsLibroMayor as cols } from '../../../components/modules/data/headersTables'

function groupRowsByCuenta(rows) {
  // Agrupa por id de cuenta, y dentro guarda el nombre
  
  const map = {}
  for (const row of rows) {
    const cuentaId = row.cuentaContableId
    const nombreCuenta = row.cuentaContable?.nombreCuenta || 'Cuenta desconocida'
    if (!map[cuentaId]) map[cuentaId] = { nombreCuenta, movimientos: [] }
    map[cuentaId].movimientos.push(row)
  }
  return Object.values(map)
}

export default function Table({
  rows,
  className,
  lastElementRef,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [order, setOrder] = useState(null)

  const orderableCols = [
    { col: 'Fecha', field: 'fecha' },
    { col: 'Glosa', field: 'glosa' },
    { col: 'Debe', field: 'debe' },
    { col: 'Haber', field: 'haber' }
  ]
  const findColConfig = col => orderableCols.find(item => item.col === col)

  const handleSort = ({ col, field }) => {
    if (!order) {
      setOrder({ col, field, direction: 'asc' })
      return
    }

    const isAsc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: isAsc ? 'desc' : 'asc' })
  }

  // Ordena todos los movimientos antes de agrupar
  const displayedRows = useMemo(() => {
    if (!order) return rows

    return [...rows].sort((a, b) => {
      const aVal = a[order.field]
      const bVal = b[order.field]
      if (order.field === 'fecha') {
        const dateA = aVal ? new Date(aVal) : new Date(0)
        const dateB = bVal ? new Date(bVal) : new Date(0)
        return order.direction === 'asc' ? dateA - dateB : dateB - dateA
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order.direction === 'asc' ? aVal - bVal : bVal - aVal
      }
      const strA = aVal ? aVal.toString().toLowerCase() : ''
      const strB = bVal ? bVal.toString().toLowerCase() : ''
      if (strA < strB) return order.direction === 'asc' ? -1 : 1
      if (strA > strB) return order.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [rows, order])

  // Agrupa movimientos por cuenta
  const cuentas = useMemo(() => groupRowsByCuenta(displayedRows), [displayedRows])

  if (!rows?.length) {
    return <Text className='grid place-items-center py-12 text-muted font-sans'>No hay datos</Text>
  }

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className='rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0'>
        <div className='text-[11px] text-muted'>{label}</div>
        <div className='text-sm font-semibold text-text break-words'>{value}</div>
      </div>
    )

    return (
      <div className={clsx('space-y-3 font-sans text-text', className)}>
        {cuentas.map(({ nombreCuenta, movimientos }, idxCuenta) => (
          <Fragment key={`${nombreCuenta}-${idxCuenta}`}>
            <div className='rounded-2xl border border-border bg-surface/70 p-3'>
              <div className='text-xs font-semibold text-text'>{nombreCuenta}</div>
            </div>
            {movimientos.map((row, i) => (
              <div key={row.id || i} className='rounded-2xl border border-border bg-surface p-3'>
                <div className='text-xs font-semibold text-text'>
                  {row.comprobante || 'Comprobante'}
                </div>
                <div className='mt-1 text-[11px] text-muted'>
                  {row.fecha ? formatDate(row.fecha) : ''} ·{' '}
                  {row.tipoComprobante?.nombreTipoComprobante || row.tipoComprobante?.name || ''}
                </div>
                <div className='mt-1 text-[11px] text-muted'>
                  {row.informacion?.rutEmpresa || ''} {row.informacion?.razonSocial || ''}
                </div>
                <div className='mt-1 text-[11px] text-muted'>
                  {row.numero ? `Doc ${row.numero}` : ''}
                </div>
                <div className='mt-1 text-[11px] text-muted'>
                  {row.glosa || ''}
                </div>
                <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                  {renderMetric('Debe', formatCurrencySinSignoPeso(row.debe))}
                  {renderMetric('Haber', formatCurrencySinSignoPeso(row.haber))}
                  {renderMetric('Saldo', formatCurrencySinSignoPeso(row.saldo))}
                </div>
              </div>
            ))}
          </Fragment>
        ))}

        {hasNextPage && (
          <div ref={lastElementRef} className='flex justify-center py-4 flex-shrink-0'>
            {isFetchingNextPage ? (
              <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
            ) : (
              <button
                onClick={onLoadMore}
                className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
              >
                Cargar más
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='border border-border rounded bg-surface font-sans text-[10px] sm:text-[11px] text-text leading-5 overflow-hidden'>
      <div className='overflow-x-auto'>
        <div className={clsx('overflow-y-auto', className || 'max-h-[60vh]')}>
      <table className='table-report-auto min-w-[1060px]' style={{ tableLayout: 'auto' }}>
        <thead className='sticky top-0 bg-surface z-10'>
          <tr>
            {cols.map((col, i) => {
              const cfg = findColConfig(col)
              const isTipo = col === 'Tipo'
              const isRazon = col === 'Razon Social'
              const isGlosa = col === 'Glosa'
              const widthClass = isTipo ? 'w-[130px]' : isRazon ? 'w-[170px]' : isGlosa ? 'w-[190px]' : ''
              return (
                <th
                  key={i}
                  className={clsx(
                    'table-report-th text-center align-middle',
                    widthClass,
                    cfg && 'cursor-pointer select-none'
                  )}
                  onClick={() => cfg && handleSort(cfg)}
                >
                  <div className='flex w-full min-h-[34px] flex-col items-center justify-center leading-tight'>
                    <span className={clsx(isTipo || isRazon || isGlosa ? 'whitespace-normal break-words text-center' : 'whitespace-nowrap text-center')}>
                      {col}
                    </span>
                    {cfg && (
                      <KeyboardArrowDownIcon
                        className={clsx('mt-0.5 transition-transform', {
                          'opacity-40': !order || order.col !== col,
                          'rotate-180': order && order.col === col && order.direction === 'desc'
                        })}
                        fontSize='small'
                      />
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {cuentas.map(({ nombreCuenta, movimientos }, idxCuenta) => (
            <Fragment key={nombreCuenta + idxCuenta}>
              {/* Fila con el nombre de la cuenta (ocupa todas las columnas) */}
              <tr className='table-report-section'>
                <td colSpan={cols.length} className='table-report-td text-left font-semibold whitespace-normal break-words'>
                  {nombreCuenta}
                </td>
              </tr>
              {movimientos.map((row, i) => (
                <tr key={row.id || i} className='table-report-row'>
                  {/* Fecha */}
                  <td className='table-report-td text-center align-top'>
                    <Text>{row.fecha && formatDate(row.fecha)}</Text>
                  </td>
                  {/* Tipo */}
                  <td className='table-report-td text-center align-top w-[130px] whitespace-normal break-words leading-tight'>
                    <Text>
                      {row.tipoComprobante?.nombreTipoComprobante || row.tipoComprobante?.name}
                    </Text>
                  </td>
                  {/* Comprobante */}
                  <td className='table-report-td text-center align-top'>
                    <Text>{row.comprobante}</Text>
                  </td>
                  {/* RUT */}
                  <td className='table-report-td text-center align-top whitespace-nowrap'>
                    <Text>{row.informacion?.rutEmpresa}</Text>
                  </td>
                  {/* Razón Social */}
                  <td className='px-3 py-2 align-top w-[170px] whitespace-normal break-words leading-tight text-center'>
                    <Text>{row.informacion?.razonSocial}</Text>
                  </td>
                  {/* Documento */}
                  <td className='table-report-td text-center align-top'>
                    <Text>{row.numero}</Text>
                  </td>
                  {/* Debe */}
                  <td className='table-report-td text-right align-top'>
                    <Text>{formatCurrencySinSignoPeso(row.debe)}</Text>
                  </td>
                  {/* Haber */}
                  <td className='table-report-td text-right align-top'>
                    <Text>{formatCurrencySinSignoPeso(row.haber)}</Text>
                  </td>
                  {/* Saldo */}
                  <td className='table-report-td text-right align-top'>
                    <Text>{formatCurrencySinSignoPeso(row.saldo)}</Text>
                  </td>
                  {/* Glosa */}
                  <td className='px-3 py-2 align-top w-[190px] whitespace-normal break-words leading-tight text-center'>
                    <Text>{row.glosa}</Text>
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={lastElementRef} className='flex justify-center py-4 flex-shrink-0'>
          {isFetchingNextPage ? (
            <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
          ) : (
            <button
              onClick={onLoadMore}
              className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
            >
              Cargar más
            </button>
          )}
        </div>
      )}
        </div>
      </div>
    </div>
  )
}
